/**
 * 🗂 Activities — the library panel: every activity in the trip, searchable,
 * with per-slot offering toggles.
 *
 * "Selected" here means *offered in a dropdown*, which is different from
 * *picked* (slot state). A chip per offering slot shows where each activity is
 * available; clicking a chip withdraws it from that slot, the "offer in…"
 * select adds it to another. A slot's current pick and its default are
 * protected server-side — the panel surfaces those errors as banners rather
 * than pre-computing every rule.
 */
import type { DefOp } from "@trip/shared/defops";
import { esc } from "@trip/shared/engine/time";
import { isFixed } from "@trip/shared/types";
import { store } from "../store.js";
import { banner } from "./banner.js";

const auth = (extra: Record<string, string> = {}) => ({
  ...extra,
  ...(store.key ? { "x-trip-key": store.key } : {}),
});

async function postOps(ops: DefOp[]): Promise<boolean> {
  try {
    const res = await fetch(`/api/trip/${encodeURIComponent(store.tripId)}/def`, {
      method: "POST",
      headers: auth({ "content-type": "application/json" }),
      body: JSON.stringify({ baseRev: store.rev, ops }),
    });
    const data = (await res.json()) as { error?: string; code?: string };
    if (!res.ok) {
      banner(data.code === "stale" ? "The trip changed under you — reloading." : (data.error ?? "That change was refused."), "warn");
      if (data.code === "stale") setTimeout(() => location.reload(), 1200);
      return false;
    }
    return true;
  } catch {
    banner("Couldn't reach the server.", "warn");
    return false;
  }
}

interface SlotRef {
  dayId: string;
  index: number;
  label: string;
}

function allSlots(): SlotRef[] {
  const out: SlotRef[] = [];
  for (const d of store.def.days) {
    d.items.forEach((it, i) => {
      if (!isFixed(it)) out.push({ dayId: d.id, index: i, label: `${d.dow} ${d.date} · ${it.s}` });
    });
  }
  return out;
}

export function openActivitiesPanel(filter = "") {
  const def = store.def;
  const canEdit = store.role === "edit";
  const slots = allSlots();
  const offeredIn = new Map<string, SlotRef[]>();
  for (const s of slots) {
    const item = def.days.find((d) => d.id === s.dayId)!.items[s.index];
    if (isFixed(item)) continue;
    for (const o of item.opts) {
      if (!offeredIn.has(o)) offeredIn.set(o, []);
      offeredIn.get(o)!.push(s);
    }
  }
  const picked = new Set(Object.values(store.slots).map((v) => v.act));

  const q = filter.trim().toLowerCase();
  const groups = new Map<string, [string, (typeof def.act)[string]][]>();
  for (const [id, a] of Object.entries(def.act)) {
    if (a.nomodal) continue; // sentinels aren't library content
    if (q && !`${a.name} ${a.cat ?? ""} ${id}`.toLowerCase().includes(q)) continue;
    const cat = a.cat ?? "Other";
    if (!groups.has(cat)) groups.set(cat, []);
    groups.get(cat)!.push([id, a]);
  }

  const modal = document.getElementById("modal")!;
  modal.style.maxWidth = "700px";
  modal.innerHTML = `
    <div class="m-head">
      <div class="m-cat">Library</div><h3>🗂 Activities</h3>
      <button class="m-close" data-action="close" aria-label="close">✕</button>
    </div>
    <div class="m-body">
      <div class="act-toolrow">
        <input id="act-search" type="search" placeholder="Search ${Object.keys(def.act).length} activities…" value="${esc(filter)}">
        ${canEdit ? `<button class="btn-cta" data-action="ed-act-new" style="white-space:nowrap">＋ New</button>` : ""}
      </div>
      <div class="act-panel">
        ${[...groups.entries()]
          .sort(([a], [b]) => a.localeCompare(b))
          .map(
            ([cat, acts]) => `
          <div class="act-group">
            <div class="act-cat">${esc(cat)}</div>
            ${acts
              .map(([id, a]) => {
                const offers = offeredIn.get(id) ?? [];
                const origin = def.actOrigin?.[id] ?? "extracted";
                return `
              <div class="act-row" data-act="${esc(id)}">
                <div class="act-head">
                  <span class="act-name">${esc(a.name)}</span>
                  ${picked.has(id) ? `<span class="badge b-edit" title="currently picked in a slot">picked</span>` : ""}
                  ${origin !== "extracted" ? `<span class="badge b-view">${origin === "ai" ? "✨ added" : "added"}</span>` : ""}
                  <button class="btn-det btn-sm" data-action="modal" data-id="${esc(id)}">ⓘ</button>
                  ${canEdit && origin !== "extracted" ? `<button class="btn-det act-del" data-action="ed-act-edit" data-id="${esc(id)}" title="Edit details and pin">✎</button>` : ""}
                  ${
                    canEdit && origin !== "extracted" && !offers.length
                      ? `<button class="btn-det act-del" data-action="act-remove" data-id="${esc(id)}">🗑</button>`
                      : ""
                  }
                </div>
                <div class="act-offers">
                  ${offers
                    .map(
                      (s) =>
                        `<button class="act-chip${canEdit ? "" : " ro"}" ${canEdit ? `data-action="act-unoffer" data-id="${esc(id)}" data-day="${s.dayId}" data-i="${s.index}" title="Click to stop offering it here"` : ""}>${esc(s.label)}${canEdit ? " ✕" : ""}</button>`
                    )
                    .join("")}
                  ${
                    canEdit
                      ? `<select class="act-offer-sel" data-action="act-offer" data-id="${esc(id)}">
                          <option value="">＋ offer in…</option>
                          ${slots
                            .filter((s) => !offers.some((o) => o.dayId === s.dayId && o.index === s.index))
                            .map((s) => `<option value="${s.dayId}:${s.index}">${esc(s.label)}</option>`)
                            .join("")}
                        </select>`
                      : ""
                  }
                </div>
              </div>`;
              })
              .join("")}
          </div>`
          )
          .join("")}
      </div>
    </div>`;
  document.getElementById("modalbg")!.classList.add("open");

  document.getElementById("act-search")!.addEventListener("input", (e) => {
    const val = (e.target as HTMLInputElement).value;
    // re-render preserving focus: cheap enough at this catalog size
    const pos = (e.target as HTMLInputElement).selectionStart;
    openActivitiesPanel(val);
    const input = document.getElementById("act-search") as HTMLInputElement;
    input.focus();
    if (pos !== null) input.setSelectionRange(pos, pos);
  });
}

export async function offerActivity(id: string, target: string) {
  const [dayId, index] = target.split(":");
  if (await postOps([{ op: "slot.optsAdd", dayId, index: Number(index), actIds: [id] }])) {
    banner("Offered — it's in that slot's dropdown now.", "ok");
  }
}

export async function unofferActivity(id: string, dayId: string, index: number) {
  if (await postOps([{ op: "slot.optsRemove", dayId, index, actIds: [id] }])) {
    banner("Withdrawn from that slot.", "ok");
  }
}

export async function removeActivity(id: string) {
  const name = store.def.act[id]?.name ?? id;
  if (!confirm(`Remove "${name}" from the trip's library?\n\nIt isn't offered in any slot, so nothing else changes.`)) return;
  if (await postOps([{ op: "act.remove", id }])) {
    banner(`Removed ${name}.`, "ok");
  }
}
